import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowsFullscreen, XLg } from 'react-bootstrap-icons';
import NavigationRowCanvas from './NavigationRowCanvas';
import { NAVIGATION_CONFIG } from '../../../config/navigationConfig';
import {
  getNavigationData,
  getNavigationMetadata
} from '../../../services/navigationDataService';
import { NavigationStatus, isProcessingStatus, isCompletedStatus, isFailedStatus, isBlockingProcessingStatus, isNavigationDataAvailable } from '../../../constants/navigationStatus';
import { highchartsLabelFormatterWithoutMsFromMs } from '../../../utils/dateFormatter';
import './EcgNavigationCanvas.css';

// Constants
const MAX_VISIBLE_ROWS = 15;
const MIN_VISIBLE_ROWS = 1;
const STORAGE_KEY = 'ecg-navigation-rows-visible';

const EcgNavigationCanvas = forwardRef(({
  baseRecordUrl,
  initialViewStartMs = 0,
  initialViewEndMs = 5000,
  onNavigationClick,
  useActualTime = false,
  isOnlineMode = false,
  onlineTransmissionState = 0,
  onUserNavigatedAway,
}, ref) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const statusCheckIntervalRef = useRef(null);

  // Online state constants (matching parent component)
  const ONLINE_STATE_NO_ONLINE = 0;
  const ONLINE_STATE_WAITING = 10;
  const ONLINE_STATE_RECEIVING = 20;
  const ONLINE_STATE_FINISHED = 30;

  // Internal state for viewport - controlled internally, not by props
  const [currentViewStartMs, setCurrentViewStartMs] = useState(initialViewStartMs);
  const [currentViewEndMs, setCurrentViewEndMs] = useState(initialViewEndMs);

  const [loadedChunks, setLoadedChunks] = useState(new Map());
  const [visibleRows, setVisibleRows] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [navigationStatus, setNavigationStatus] = useState(null); // Will hold actual numeric status from backend
  const [isChecking, setIsChecking] = useState(true);
  const [startTimeMs, setStartTimeMs] = useState(null); // Store startTimeMs from metadata
  const [cardiacEvents, setCardiacEvents] = useState([]); // Store cardiac events from metadata
  const [isLoadingPreview, setIsLoadingPreview] = useState(false); // Block clicks while preview is loading
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rowsVisible, setRowsVisible] = useState(() => {
    // Try to load from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= MIN_VISIBLE_ROWS && parsed <= MAX_VISIBLE_ROWS) {
          return parsed;
        }
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return NAVIGATION_CONFIG.ROWS_VISIBLE; // Default value
  });

  // Check navigation status - backend handles triggering automatically
  useEffect(() => {
    if (!baseRecordUrl) return;

    const checkNavigationStatus = async () => {
      try {
        setIsChecking(true);
        
        // Check if navigation data exists (backend will trigger processing if needed)
        const metadata = await getNavigationMetadata(baseRecordUrl);
        
        if (metadata) {
          setNavigationStatus(metadata.status);
          setIsChecking(false);
          
          // Store startTimeMs for time formatting
          if (metadata.startTimeMs) {
            setStartTimeMs(metadata.startTimeMs);
          }

          // Store cardiac events for highlighting
          if (metadata.cardiacEvents) {
            setCardiacEvents(metadata.cardiacEvents);
          }

          // Calculate total rows from metadata
          if (metadata.durationMs) {
            const minutesPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW;
            const rows = Math.ceil(metadata.durationMs / (minutesPerRow * 60 * 1000));
            setTotalRows(rows);
          }

          // Start polling if still processing
          if (isBlockingProcessingStatus(metadata.status) || metadata.status === NavigationStatus.NOT_PROCESSED) {
            startStatusPolling();
          }
        } else {
          // No metadata returned - assume processing
          setNavigationStatus(NavigationStatus.START_PROCESSING);
          setIsChecking(false);
          startStatusPolling();
        }
      } catch (error) {
        console.error('Error checking navigation status:', error);
        setError(error);
        setNavigationStatus(NavigationStatus.FAILED);
        setIsChecking(false);
      }
    };

    const startStatusPolling = () => {
      statusCheckIntervalRef.current = setInterval(async () => {
        try {
          const metadata = await getNavigationMetadata(baseRecordUrl);
          if (metadata) {
            setNavigationStatus(metadata.status);

            // Store startTimeMs for time formatting
            if (metadata.startTimeMs) {
              setStartTimeMs(metadata.startTimeMs);
            }

            // Store cardiac events for highlighting
            if (metadata.cardiacEvents) {
              setCardiacEvents(metadata.cardiacEvents);
            }

            // Update total rows from metadata
            if (metadata.durationMs) {
              const minutesPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW;
              const rows = Math.ceil(metadata.durationMs / (minutesPerRow * 60 * 1000));
              setTotalRows(rows);
            }

            // Stop polling if completed or failed
            if (isNavigationDataAvailable(metadata.status) || isFailedStatus(metadata.status)) {
              clearInterval(statusCheckIntervalRef.current);
            }
          }
        } catch (error) {
          console.error('Error polling navigation status:', error);
        }
      }, 2000); // Poll every 2 seconds
    };

    checkNavigationStatus();

    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
    };
  }, [baseRecordUrl]);

  // Calculate which rows are visible
  const calculateVisibleRows = useCallback(() => {
    if (!containerRef.current) return new Set();
    
    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const rowHeight = NAVIGATION_CONFIG.ROW_HEIGHT_PX;
    
    const startRow = Math.floor(scrollTop / rowHeight);
    const endRow = Math.ceil((scrollTop + containerHeight) / rowHeight);
    
    const visible = new Set();
    for (let i = Math.max(0, startRow); i <= Math.min(totalRows - 1, endRow); i++) {
      visible.add(i);
    }
    
    return visible;
  }, [totalRows]);

  // Load navigation data for a specific minute
  const loadChunk = useCallback(async (minuteIndex) => {
    // Check current state inside setLoadedChunks to avoid stale closure
    return new Promise((resolve) => {
      setLoadedChunks(prev => {
        if (prev.has(minuteIndex)) {
          resolve(false); // Already loaded
          return prev;
        }

        const startMs = minuteIndex * NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;
        const endMs = startMs + (NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000);

        // Start loading
        getNavigationData(baseRecordUrl, startMs, endMs)
          .then(serverData => {
            // Convert server data format { data: [...], sampleRate } to chunk format
            const chunkData = {
              sampleRate: serverData.sampleRate,
              chunks: serverData.data && serverData.data.length > 0 ? [{
                startMs: 0, // Server data starts at beginning of the row
                data: serverData.data
              }] : []
            };
            setLoadedChunks(prevChunks => new Map(prevChunks).set(minuteIndex, chunkData));
            resolve(true);
          })
          .catch(err => {
            console.error(`Failed to load navigation chunk ${minuteIndex}:`, err);
            resolve(false);
          });

        return prev; // Return unchanged for now, will update when promise resolves
      });
    });
  }, [baseRecordUrl]);

  // Reload metadata for online mode - fetch fresh totalRows
  const reloadMetadataForOnlineMode = useCallback(async () => {
    try {
      console.log('EcgNavigationCanvas: Reloading metadata for online mode...');

      const metadata = await getNavigationMetadata(baseRecordUrl);

      if (metadata) {
        setNavigationStatus(metadata.status);

        if (metadata.startTimeMs) {
          setStartTimeMs(metadata.startTimeMs);
        }

        if (metadata.durationMs) {
          const minutesPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW;
          const newTotalRows = Math.ceil(metadata.durationMs / (minutesPerRow * 60 * 1000));
          setTotalRows(newTotalRows);

          console.log(`EcgNavigationCanvas: Metadata reloaded - ${newTotalRows} rows, ${metadata.durationMs}ms duration`);

          return newTotalRows;
        }
      }

      return totalRows; // Return current if reload fails
    } catch (error) {
      console.error('EcgNavigationCanvas: Error reloading metadata:', error);
      return totalRows; // Return current if error
    }
  }, [baseRecordUrl, totalRows]);

  // Load visible rows with prefetching
  const loadVisibleRows = useCallback(async () => {
    // Only load data if navigation data is available (completed or online processing)
    if (!isNavigationDataAvailable(navigationStatus)) {
      return;
    }

    const visible = calculateVisibleRows();
    setVisibleRows(visible);

    // Load visible rows and prefetch adjacent ones
    const toLoad = new Set();
    visible.forEach(row => {
      toLoad.add(row);
      // Prefetch adjacent rows
      for (let i = 1; i <= NAVIGATION_CONFIG.PREFETCH_ROWS; i++) {
        if (row - i >= 0) toLoad.add(row - i);
        if (row + i < totalRows) toLoad.add(row + i);
      }
    });

    // Load new chunks (only ones not already loaded)
    setLoading(true);
    await Promise.all(Array.from(toLoad).map(row => loadChunk(row)));
    setLoading(false);

    // After loading, unload distant rows to save memory
    // Use functional update to avoid dependency on loadedChunks
    setLoadedChunks(prev => {
      const newMap = new Map(prev);
      const minVisible = Math.min(...visible);
      const maxVisible = Math.max(...visible);

      // Check if last row is being unloaded while in online mode
      const lastRowIndex = totalRows - 1;
      const isLastRowBeingUnloaded = isOnlineMode &&
        prev.has(lastRowIndex) &&
        (lastRowIndex < minVisible - NAVIGATION_CONFIG.UNLOAD_DISTANCE ||
         lastRowIndex > maxVisible + NAVIGATION_CONFIG.UNLOAD_DISTANCE);

      if (isLastRowBeingUnloaded) {
        console.log('EcgNavigationCanvas: Last row being unloaded - user scrolled away, disabling online mode');
        if (onUserNavigatedAway) {
          onUserNavigatedAway();
        }
      }

      // Only unload rows that are far from visible area
      const unloadedRows = [];
      prev.forEach((_, key) => {
        if (key < minVisible - NAVIGATION_CONFIG.UNLOAD_DISTANCE ||
            key > maxVisible + NAVIGATION_CONFIG.UNLOAD_DISTANCE) {
          newMap.delete(key);
          unloadedRows.push(key);
        }
      });

      if (unloadedRows.length > 0) {
        console.log(`EcgNavigationCanvas: Unloaded ${unloadedRows.length} rows from memory: ${unloadedRows.join(', ')} (visible: ${minVisible}-${maxVisible})`);
      }

      return newMap;
    });
  }, [calculateVisibleRows, totalRows, loadChunk, navigationStatus, isOnlineMode, onUserNavigatedAway]);

  // Handle scroll with debouncing
  const handleScroll = useCallback(() => {
    // Update visible rows immediately for smooth scrolling
    const visible = calculateVisibleRows();
    setVisibleRows(visible);
    
    // Debounce the loading of new chunks
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      loadVisibleRows();
    }, NAVIGATION_CONFIG.LOAD_DELAY);
  }, [calculateVisibleRows, loadVisibleRows]);

  // Track if online mode has been initialized to prevent re-initialization loops
  const onlineModeInitialized = useRef(false);
  // Track if the initial preview load has been triggered to prevent duplicate loads
  const initialPreviewLoadTriggered = useRef(false);

  // Initial load when navigation becomes ready
  useEffect(() => {
    if (isNavigationDataAvailable(navigationStatus) && totalRows > 0) {
      loadVisibleRows();

      // Check if this is an online/pending record (check state directly, not isOnlineMode which may arrive later)
      const isOnlineRecord = onlineTransmissionState === ONLINE_STATE_WAITING ||
                             onlineTransmissionState === ONLINE_STATE_RECEIVING;

      // Only trigger initial load once, and only if NOT an online record
      // (online mode will load the last row instead)
      if (!initialPreviewLoadTriggered.current && !onlineModeInitialized.current && !isOnlineRecord) {
        // Schedule the load, but check again before executing
        setTimeout(() => {
          // Double-check the flag before loading (in case online state changed during setTimeout delay)
          if (!initialPreviewLoadTriggered.current) {
            initialPreviewLoadTriggered.current = true; // Mark as triggered
            console.log('EcgNavigationCanvas: Initial load - loading from beginning');
            handleRowClick(0, 0, 1000);
          } else {
            console.log('EcgNavigationCanvas: Skipping initial load - online mode was detected during delay');
          }
        }, 500);
      } else if (isOnlineRecord) {
        console.log('EcgNavigationCanvas: Skipping initial load from beginning (online record will load last row)');
        initialPreviewLoadTriggered.current = true; // Mark as handled to prevent normal load later
      }
    }
  }, [navigationStatus, totalRows, onlineTransmissionState]); // Check onlineTransmissionState instead of isOnlineMode

  // Separate effect to handle online mode activation (only when it changes to true)
  useEffect(() => {
    if (isOnlineMode && !onlineModeInitialized.current && isNavigationDataAvailable(navigationStatus)) {
      // Only initialize once - don't wait for totalRows, we'll get it from metadata
      onlineModeInitialized.current = true;

      console.log('EcgNavigationCanvas: Online mode activated - preparing for streaming');

      setTimeout(async () => {
        // Reload metadata to get fresh totalRows
        const freshTotalRows = await reloadMetadataForOnlineMode();

        // Only proceed if we got valid row count
        if (freshTotalRows > 0) {
          // Scroll container to bottom
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }

          // Load only the last row - visibility system will load others
          const lastRowIndex = freshTotalRows - 1;
          if (lastRowIndex >= 0) {
            console.log(`EcgNavigationCanvas: Loading last row (${lastRowIndex}) for online streaming`);
            await loadChunk(lastRowIndex);
          }

          // Simulate a click at the end of the last row to load preview
          const canvasWidth = 1000;
          const clickXAtEnd = canvasWidth - 1;
          // Pass forceIsLastRow=true and scrollToLastPoint=true for online mode initialization
          handleRowClick(lastRowIndex, clickXAtEnd, canvasWidth, false, true, true);
        }
      }, 500);
    }

    // Reset flag when online mode is turned off
    if (!isOnlineMode) {
      onlineModeInitialized.current = false;
    }
  }, [isOnlineMode, navigationStatus]); // Removed totalRows - only trigger on online mode or status change

  // Handle click on navigation row
  const handleRowClick = useCallback((rowIndex, clickX, canvasWidth, isUserClick = false, forceIsLastRow = false, scrollToLastPoint = false) => {
    console.log('=== EcgNavigationCanvas.handleRowClick CALLED ===');
    console.log('handleRowClick params:', { rowIndex, clickX, canvasWidth, isUserClick, forceIsLastRow, scrollToLastPoint });

    // Block clicks if preview is loading
    if (isLoadingPreview) {
      console.log('Navigation click blocked - preview chart is still loading');
      return;
    }

    // If user manually clicked on any row except the last one, disable online mode
    if (isUserClick && rowIndex !== totalRows - 1) {
      console.log('EcgNavigationCanvas: User navigated away from last row - disabling online mode');
      if (onUserNavigatedAway) {
        onUserNavigatedAway();
      }
    }

    const msPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;

    const clickRatio = clickX / canvasWidth;
    const clickTimeMs = (rowIndex * msPerRow) + (clickRatio * msPerRow);

    // Calculate the total record duration
    const recordDurationMs = totalRows * NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;

    // Calculate the center point for the view window
    // The view window width is currentViewEndMs - currentViewStartMs
    const viewWindowWidth = currentViewEndMs - currentViewStartMs;
    let viewStartTimeMs = clickTimeMs - (viewWindowWidth / 2);

    // Apply boundary constraints
    // Ensure the view window doesn't go before the start of the record
    if (viewStartTimeMs < 0) {
      viewStartTimeMs = 0;
    }
    // Ensure the view window doesn't go past the end of the record
    else if (viewStartTimeMs + viewWindowWidth > recordDurationMs) {
      viewStartTimeMs = recordDurationMs - viewWindowWidth;
    }

    // Calculate which rows the view window spans
    const viewEndTimeMs = viewStartTimeMs + viewWindowWidth - 1;
    const startRowIndex = Math.floor(viewStartTimeMs / msPerRow);
    const endRowIndex = Math.floor(viewEndTimeMs / msPerRow);

    // Get the actual row boundaries that the view spans
    const rowStartMs = startRowIndex * msPerRow;
    const rowEndMs = (endRowIndex + 1) * msPerRow; // End of the last row the view touches

    // Check if this is the last row
    // Use forceIsLastRow when we know for sure it's the last row (e.g., when a new row was just added)
    // This avoids race conditions with React state updates
    const isLastRow = forceIsLastRow || (endRowIndex === totalRows - 1);

    if (onNavigationClick) {
      // Set loading state to block further clicks
      setIsLoadingPreview(true);

      // Apply actual time conversion here if needed
      let startTime = rowStartMs;
      let endTime = rowEndMs; // Full row boundary (even for last row)
      let centerTime = clickTimeMs;

      if (useActualTime && startTimeMs) {
        // Convert to actual timestamps
        startTime = startTimeMs + rowStartMs;
        endTime = startTimeMs + rowEndMs;
        centerTime = startTimeMs + clickTimeMs;
      }

      // Pass the final calculated times, isLastRow flag, and scrollToLastPoint flag
      // For last row, endTime is the full row boundary, allowing empty space to be shown
      console.log('EcgNavigationCanvas: Calling onNavigationClick with:', {
        viewStartTimeMs,
        startTime,
        endTime,
        centerTime,
        isLastRow,
        scrollToLastPoint
      });
      onNavigationClick(viewStartTimeMs, startTime, endTime, centerTime, isLastRow, scrollToLastPoint);
    }

    // Exit fullscreen after clicking a row and scroll inline container to show clicked row
    if (isFullscreen) {
      setIsFullscreen(false);
      setTimeout(() => {
        if (containerRef.current) {
          const rowTop = rowIndex * NAVIGATION_CONFIG.ROW_HEIGHT_PX;
          containerRef.current.scrollTop = Math.max(0,
            rowTop - (containerRef.current.clientHeight / 2));
        }
      }, 50);
    }

    console.log('=== EcgNavigationCanvas.handleRowClick END ===');
  }, [onNavigationClick, currentViewStartMs, currentViewEndMs, totalRows, startTimeMs, useActualTime, isLoadingPreview, onUserNavigatedAway, loadedChunks, isFullscreen]);

  // Calculate view window position for each row
  const calculateViewWindow = useCallback((rowIndex) => {
    const rowStartMs = rowIndex * NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;
    const rowEndMs = rowStartMs + (NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000);
    
    // Convert view times to zero-based if we're in actual time mode
    let viewStartMs = currentViewStartMs;
    let viewEndMs = currentViewEndMs;
    
    if (useActualTime && startTimeMs) {
      // Convert from actual time to zero-based time
      viewStartMs = currentViewStartMs - startTimeMs;
      viewEndMs = currentViewEndMs - startTimeMs;
    }
    
    // Check if current view intersects with this row
    if (viewStartMs >= rowEndMs || viewEndMs <= rowStartMs) {
      return null; // No intersection
    }
    
    // Calculate intersection
    const intersectStart = Math.max(viewStartMs, rowStartMs);
    const intersectEnd = Math.min(viewEndMs, rowEndMs);
    
    // Convert to relative positions (0-1)
    const startRatio = (intersectStart - rowStartMs) / (rowEndMs - rowStartMs);
    const endRatio = (intersectEnd - rowStartMs) / (rowEndMs - rowStartMs);
    
    return {
      startRatio: Math.max(0, Math.min(1, startRatio)),
      endRatio: Math.max(0, Math.min(1, endRatio)),
    };
  }, [currentViewStartMs, currentViewEndMs, useActualTime, startTimeMs]);

  // Format timestamp for row label
  const formatTimestamp = useCallback((rowIndex) => {
    const minuteStartMs = rowIndex * NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;
    
    if (useActualTime && startTimeMs) {
      // Use the actual time (add offset to base time)
      return highchartsLabelFormatterWithoutMsFromMs(startTimeMs + minuteStartMs);
    } else {
      // Use zero-based time
      return highchartsLabelFormatterWithoutMsFromMs(minuteStartMs);
    }
  }, [useActualTime, startTimeMs]);

  // Handle changing number of visible rows
  const handleRowsChange = useCallback((delta) => {
    setRowsVisible(prev => {
      const newValue = prev + delta;
      // Limit between min and max rows
      const finalValue = Math.max(MIN_VISIBLE_ROWS, Math.min(MAX_VISIBLE_ROWS, newValue));
      
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, finalValue.toString());
      } catch (e) {
        // Ignore localStorage errors
      }
      
      return finalValue;
    });
  }, []);

  // Escape key handler for fullscreen mode
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Recalculate visible rows when fullscreen changes (container height changes)
  useEffect(() => {
    if (containerRef.current) {
      loadVisibleRows();
    }
  }, [isFullscreen]); // loadVisibleRows intentionally omitted from deps to avoid loops

  // Append navigation chunk data for online streaming
  // Note: Gaps are acceptable and expected when metadata reload happens or navigation processing is delayed.
  // NavigationRowCanvas handles partial data gracefully - it will render whatever data is available
  // and leave gaps as empty space on the canvas. Empty rows will show just the grid.
  const appendNavigationChunk = useCallback((navigationChunk) => {
    console.log('=== EcgNavigationCanvas.appendNavigationChunk CALLED ===', navigationChunk);

    if (!navigationChunk || !navigationChunk.data || navigationChunk.data.length === 0) {
      console.log('EcgNavigationCanvas: Skipping empty navigation chunk');
      return;
    }

    const { startMs, sampleRate, data } = navigationChunk;
    console.log(`EcgNavigationCanvas: Processing chunk - startMs=${startMs}, sampleRate=${sampleRate}, data.length=${data.length}`);

    // Calculate which row this chunk belongs to
    const msPerRow = NAVIGATION_CONFIG.MINUTES_PER_ROW * 60 * 1000;
    const durationMs = (data.length / sampleRate) * 1000;
    const endMs = startMs + durationMs;
    const startRowIndex = Math.floor(startMs / msPerRow);
    const endRowIndex = Math.floor(endMs / msPerRow);

    console.log(`EcgNavigationCanvas: Appending navigation chunk - ${startMs}ms to ${endMs}ms (${data.length} samples at ${sampleRate}Hz), rows ${startRowIndex} to ${endRowIndex}`);

    // Check if chunk extends beyond current totalRows - need to add new rows
    const isNewRowAdded = endRowIndex >= totalRows;
    const currentTotalRows = totalRows; // Capture current value before updating
    if (isNewRowAdded) {
      const newTotalRows = endRowIndex + 1;
      const previousRowIndex = totalRows - 1; // The row that was just completed (last row before new one)
      console.log(`EcgNavigationCanvas: Extending navigation rows from ${totalRows} to ${newTotalRows}`);
      setTotalRows(newTotalRows);

      // If in online mode, auto-scroll the navigation container to show the new row
      if (isOnlineMode) {
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
          console.log(`EcgNavigationCanvas: Auto-scrolled navigation to show new row ${endRowIndex}`);
          // Note: Preview chart will auto-scroll itself as new data arrives
        }, 200); // Delay to let DOM update and data append complete

        // Reload the previous row from server to replace any streaming artifacts
        if (previousRowIndex >= 0) {
          console.log(`EcgNavigationCanvas: Reloading previous row ${previousRowIndex} to remove artifacts`);
          setTimeout(async () => {
            // Clear cached data for this row to force fresh load from server
            setLoadedChunks(prev => {
              const newMap = new Map(prev);
              newMap.delete(previousRowIndex);
              return newMap;
            });
            // Now load fresh data from server
            await loadChunk(previousRowIndex);
            console.log(`EcgNavigationCanvas: Refreshed row ${previousRowIndex} from server`);
          }, 500); // Delay to ensure data append completes first
        }
      }
    }

    // Store chunks with their time positions instead of concatenating
    setLoadedChunks(prev => {
      console.log(`EcgNavigationCanvas: Current loaded rows: [${Array.from(prev.keys()).sort((a, b) => a - b).join(', ')}]`);
      const newMap = new Map(prev);

      if (startRowIndex === endRowIndex) {
        // Simple case: chunk fits entirely in one row
        const rowStartMs = startRowIndex * msPerRow;
        const chunkStartMsInRow = startMs - rowStartMs; // Position within this row

        // Only append if:
        // 1. Row is currently loaded in memory, OR
        // 2. Row is newly created (>= currentTotalRows)
        // Skip appending to old rows that were unloaded - they'll reload from server when visible
        const isNewlyCreatedRow = startRowIndex >= currentTotalRows;
        if (!prev.has(startRowIndex) && !isNewlyCreatedRow) {
          console.log(`EcgNavigationCanvas: Skipping chunk append to unloaded row ${startRowIndex} - will reload from server when visible`);
          return prev; // Don't modify the map
        }

        // Additional check: If this row is loaded but far from visible area, unload it instead of appending
        // This handles the case where user scrolled away but row hasn't been unloaded yet due to debounce delay
        const currentVisible = calculateVisibleRows();
        if (currentVisible.size > 0) {
          const minVisible = Math.min(...currentVisible);
          const maxVisible = Math.max(...currentVisible);
          const isFarFromVisible = startRowIndex < minVisible - NAVIGATION_CONFIG.UNLOAD_DISTANCE ||
                                   startRowIndex > maxVisible + NAVIGATION_CONFIG.UNLOAD_DISTANCE;

          if (isFarFromVisible && !isNewlyCreatedRow && prev.has(startRowIndex)) {
            console.log(`EcgNavigationCanvas: Row ${startRowIndex} is far from visible area (${minVisible}-${maxVisible}), unloading instead of appending chunk`);
            newMap.delete(startRowIndex);
            return newMap;
          }
        }

        const existingData = prev.get(startRowIndex) || { chunks: [], sampleRate };

        // Add this chunk with its time position
        const newChunk = {
          startMs: chunkStartMsInRow,
          data: data
        };

        const updatedData = {
          sampleRate,
          chunks: [...(existingData.chunks || []), newChunk]
        };

        newMap.set(startRowIndex, updatedData);
        console.log(`EcgNavigationCanvas: Added chunk to row ${startRowIndex} at ${chunkStartMsInRow}ms, ${data.length} samples`);
      } else {
        // Complex case: chunk spans multiple rows - need to split the data
        console.log(`EcgNavigationCanvas: Chunk spans rows ${startRowIndex} to ${endRowIndex}, splitting data`);

        // Check if rows are loaded or newly created
        const firstRowLoaded = prev.has(startRowIndex);
        const lastRowLoaded = prev.has(endRowIndex);
        const firstRowIsNew = startRowIndex >= currentTotalRows;
        const lastRowIsNew = endRowIndex >= currentTotalRows;

        // Skip if either row is an old unloaded row (not newly created)
        if ((!firstRowLoaded && !firstRowIsNew) || (!lastRowLoaded && !lastRowIsNew)) {
          console.log(`EcgNavigationCanvas: Skipping multi-row chunk append - firstRow ${startRowIndex} (loaded: ${firstRowLoaded}, new: ${firstRowIsNew}), lastRow ${endRowIndex} (loaded: ${lastRowLoaded}, new: ${lastRowIsNew})`);
          return prev; // Don't modify if either row is an old unloaded row
        }

        // Additional check: If rows are far from visible area, unload them instead of appending
        const currentVisible = calculateVisibleRows();
        if (currentVisible.size > 0) {
          const minVisible = Math.min(...currentVisible);
          const maxVisible = Math.max(...currentVisible);

          const firstRowFar = startRowIndex < minVisible - NAVIGATION_CONFIG.UNLOAD_DISTANCE ||
                             startRowIndex > maxVisible + NAVIGATION_CONFIG.UNLOAD_DISTANCE;
          const lastRowFar = endRowIndex < minVisible - NAVIGATION_CONFIG.UNLOAD_DISTANCE ||
                            endRowIndex > maxVisible + NAVIGATION_CONFIG.UNLOAD_DISTANCE;

          if ((firstRowFar || lastRowFar) && (!firstRowIsNew && !lastRowIsNew)) {
            console.log(`EcgNavigationCanvas: Multi-row chunk rows ${startRowIndex}-${endRowIndex} are far from visible area (${minVisible}-${maxVisible}), unloading`);
            if (firstRowFar && prev.has(startRowIndex)) newMap.delete(startRowIndex);
            if (lastRowFar && prev.has(endRowIndex)) newMap.delete(endRowIndex);
            return newMap;
          }
        }

        // Calculate time boundaries for splitting
        const endOfFirstRowMs = (startRowIndex + 1) * msPerRow;
        const startOfLastRowMs = endRowIndex * msPerRow;

        // Calculate how many samples belong to first row
        const firstRowDurationMs = endOfFirstRowMs - startMs;
        const firstRowSampleCount = Math.floor((firstRowDurationMs / 1000) * sampleRate);

        // Split the data array
        const firstRowData = data.slice(0, firstRowSampleCount);
        const lastRowData = data.slice(firstRowSampleCount);

        console.log(`EcgNavigationCanvas: First row ${startRowIndex} gets ${firstRowData.length} samples (${firstRowDurationMs}ms)`);
        console.log(`EcgNavigationCanvas: Last row ${endRowIndex} gets ${lastRowData.length} samples`);

        // Add chunk to first row
        if (firstRowData.length > 0) {
          const rowStartMs = startRowIndex * msPerRow;
          const chunkStartMsInRow = startMs - rowStartMs;

          const existingFirstRow = prev.get(startRowIndex) || { chunks: [], sampleRate };
          const newChunk = {
            startMs: chunkStartMsInRow,
            data: firstRowData
          };

          const updatedFirstRow = {
            sampleRate,
            chunks: [...(existingFirstRow.chunks || []), newChunk]
          };

          newMap.set(startRowIndex, updatedFirstRow);
          console.log(`EcgNavigationCanvas: Row ${startRowIndex} added chunk at ${chunkStartMsInRow}ms`);
        }

        // Add chunk to last row (starts at beginning of that row)
        if (lastRowData.length > 0) {
          const existingLastRow = prev.get(endRowIndex) || { chunks: [], sampleRate };
          const newChunk = {
            startMs: 0, // Starts at beginning of the last row
            data: lastRowData
          };

          const updatedLastRow = {
            sampleRate,
            chunks: [...(existingLastRow.chunks || []), newChunk]
          };

          newMap.set(endRowIndex, updatedLastRow);
          console.log(`EcgNavigationCanvas: Row ${endRowIndex} added chunk at 0ms`);
        }
      }

      return newMap;
    });

    console.log('=== EcgNavigationCanvas.appendNavigationChunk COMPLETE ===');
    // No need to manually trigger re-render - the loadedChunks Map reference changed,
    // which will cause React to re-render the row components that use the updated data
  }, [totalRows, calculateVisibleRows, isOnlineMode, handleRowClick]);

  // Log navigation chunk data for online preview mode
  const logNavigationChunkForOnlineMode = useCallback((navigationChunk) => {
    // Always append navigation chunks when they arrive, regardless of online mode
    // The isOnlineMode flag only affects auto-scrolling behavior, not data collection
    if (navigationChunk) {
      console.log('EcgNavigationCanvas: Received navigation chunk:', navigationChunk);
      appendNavigationChunk(navigationChunk);
    }
  }, [appendNavigationChunk]);

  // Navigate to the last row programmatically
  const navigateToLastRow = useCallback(() => {
    console.log('=== EcgNavigationCanvas.navigateToLastRow CALLED ===');
    console.log('EcgNavigationCanvas: totalRows =', totalRows);

    if (totalRows === 0) {
      console.warn('EcgNavigationCanvas: Cannot navigate to last row - no rows available');
      return;
    }

    const lastRowIndex = totalRows - 1;
    const canvasWidth = 1000; // Standard width
    const clickX = 0;// canvasWidth / 2; // Click in the middle of the row

    console.log(`EcgNavigationCanvas: Programmatically navigating to last row ${lastRowIndex}`);

    // Scroll navigation container to show the last row
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      console.log('EcgNavigationCanvas: Scrolled container to bottom');
    }

    console.log('EcgNavigationCanvas: Calling handleRowClick with scrollToLastPoint=true');
    // Navigate to the last row (force it as last row to avoid race conditions)
    // Pass scrollToLastPoint=true to center viewport on last data point
    handleRowClick(lastRowIndex, clickX, canvasWidth, false, true, true);
    console.log('=== EcgNavigationCanvas.navigateToLastRow END ===');
  }, [totalRows, handleRowClick]);

  // Expose imperative handle to allow parent to update viewport without re-rendering
  useImperativeHandle(ref, () => ({
    updateViewport(startMs, endMs) {
      setCurrentViewStartMs(startMs);
      setCurrentViewEndMs(endMs);
    },
    clearLoadingState() {
      setIsLoadingPreview(false);
    },
    logNavigationChunkForOnlineMode(navigationChunk) {
      logNavigationChunkForOnlineMode(navigationChunk);
    },
    navigateToLastRow() {
      navigateToLastRow();
    }
  }), [logNavigationChunkForOnlineMode, navigateToLastRow]);

  return (
    <div className={`ecg-navigation-canvas${isFullscreen ? ' ecg-navigation-canvas-fullscreen' : ''}`}>
      <div className="navigation-header">
        <h5>{t('recordView.multiChart.ecg.navigation.title')}</h5>
        <div className="navigation-header-controls">
          {loading && <span className="loading-indicator">{t('common.isLoading')}</span>}
          {isBlockingProcessingStatus(navigationStatus) && (
            <span className="processing-indicator">{t('recordView.multiChart.ecg.navigation.processing')}</span>
          )}
          <div className="navigation-row-controls">
            {isFullscreen ? (
              <button
                className="btn-row-control"
                onClick={() => setIsFullscreen(false)}
                title={t('common.closeButton')}
              >
                <XLg size={12} />
              </button>
            ) : (
              <>
                <button
                  className="btn-row-control btn-row-up"
                  onClick={() => handleRowsChange(-1)}
                  disabled={rowsVisible <= MIN_VISIBLE_ROWS}
                  title={t('recordView.multiChart.ecg.navigation.decreaseRows')}
                >
                  ▲
                </button>
                <button
                  className="btn-row-control btn-row-down"
                  onClick={() => handleRowsChange(1)}
                  disabled={rowsVisible >= MAX_VISIBLE_ROWS}
                  title={t('recordView.multiChart.ecg.navigation.increaseRows')}
                >
                  ▼
                </button>
                <button
                  className="btn-row-control"
                  onClick={() => setIsFullscreen(true)}
                  title={t('chart.viewFullScreenButton')}
                >
                  <ArrowsFullscreen size={12} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {isChecking && (
        <div className="navigation-status-message">
          {t('common.isLoading')}
        </div>
      )}

      {!isChecking && isBlockingProcessingStatus(navigationStatus) && (
        <div className="navigation-status-message">
          {t('recordView.multiChart.ecg.navigation.processing')}
        </div>
      )}
      
      {!isChecking && isFailedStatus(navigationStatus) && (
        <div className="navigation-error">
          {t('recordView.multiChart.ecg.navigation.error')}: {error?.message || 'Processing failed'}
        </div>
      )}
      
      {!isChecking && isNavigationDataAvailable(navigationStatus) && (
        <div
          ref={containerRef}
          className="navigation-rows-container"
          onScroll={handleScroll}
          style={{
            height: isFullscreen
              ? 'calc(100vh - 70px)'
              : `${rowsVisible * NAVIGATION_CONFIG.ROW_HEIGHT_PX}px`
          }}
        >
          <div className="navigation-rows-wrapper" style={{ height: `${totalRows * NAVIGATION_CONFIG.ROW_HEIGHT_PX}px` }}>
            {Array.from({ length: totalRows }, (_, index) => {
              const isVisible = visibleRows.has(index);
              const data = loadedChunks.get(index);
              const viewWindow = calculateViewWindow(index);
              
              return (
                <div key={index} className="navigation-row" style={{ 
                  top: `${index * NAVIGATION_CONFIG.ROW_HEIGHT_PX}px`,
                  height: `${NAVIGATION_CONFIG.ROW_HEIGHT_PX}px`
                }}>
                  <div className="navigation-row-label">
                    {formatTimestamp(index)}
                  </div>
                  <div className="navigation-row-canvas-container">
                    {isVisible && (
                      <NavigationRowCanvas
                        rowIndex={index}
                        data={data}
                        viewWindow={viewWindow}
                        cardiacEvents={cardiacEvents}
                        onClick={(clickX, canvasWidth) => handleRowClick(index, clickX, canvasWidth, true)}
                        disabled={isLoadingPreview}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

EcgNavigationCanvas.displayName = 'EcgNavigationCanvas';

export default EcgNavigationCanvas;